// Your account: name, password, two-factor sign-in, recovery codes and signed-in devices.
import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { del, get, patch, post, put } from '../api';
import { useAuth } from '../auth';
import { Modal, ago, useConfirm, useToast } from '../ui';
import { ROLE_LABELS } from '../types';
import { RecoveryCodes } from './Auth';
import { Page, useCrumbs } from './Shell';
import { ExportsCard } from './Exports';
import { SSO_ERRORS, takeSsoHash } from './Auth';

interface Device { id: string; created_at: string; last_seen_at: string; ip: string | null; user_agent: string | null; current: boolean }
interface Security { totp_enabled: boolean; recovery_codes_left: number; sessions: Device[] }

function deviceName(ua: string | null): string {
  if (!ua) return 'Unknown device';
  const os = /iPad/.test(ua) ? 'iPad' : /iPhone/.test(ua) ? 'iPhone' : /Android/.test(ua) ? 'Android' : /Mac OS X/.test(ua) ? 'Mac' : /Windows/.test(ua) ? 'Windows' : /Linux/.test(ua) ? 'Linux' : 'Device';
  const browser = /Edg\//.test(ua) ? 'Edge' : /Firefox\//.test(ua) ? 'Firefox' : /CriOS|Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : 'browser';
  return `${browser} on ${os}`;
}

export function Account() {
  const { me, refresh, can } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const setCrumbs = useCrumbs();
  const { confirm, dialog } = useConfirm();
  useEffect(() => setCrumbs([{ label: 'Account' }]), [setCrumbs]);
  const { data: sec } = useQuery({ queryKey: ['security'], queryFn: () => get<Security>('/api/auth/security') });
  const [name, setName] = useState(me?.name ?? '');
  const [pw, setPw] = useState({ current: '', new: '' });
  const [regenerating, setRegenerating] = useState(false);

  const saveName = useMutation({
    mutationFn: () => patch('/api/auth/me', { name }),
    onSuccess: () => { refresh(); toast('Name saved'); },
    onError: (e: Error) => toast(e.message, true),
  });
  const setEmail = useMutation({
    mutationFn: ({ key, on }: { key: string; on: boolean }) => put('/api/auth/emails', { prefs: { [key]: on } }),
    onSuccess: (_, v) => { refresh(); toast(v.on ? 'Email turned on' : 'Email turned off'); },
    onError: (e: Error) => toast(e.message, true),
  });
  const changePw = useMutation({
    mutationFn: () => post('/api/auth/password', pw),
    onSuccess: () => { setPw({ current: '', new: '' }); qc.invalidateQueries({ queryKey: ['security'] }); toast('Password changed. Other devices were signed out.'); },
    onError: (e: Error) => toast(e.message, true),
  });
  const signOutDevice = async (d: Device) => {
    if (!(await confirm(`Sign out ${deviceName(d.user_agent)}?`, { ok: 'Sign out', danger: true }))) return;
    try { await del(`/api/auth/sessions/${d.id}`); qc.invalidateQueries({ queryKey: ['security'] }); } catch (e) { toast((e as Error).message, true); }
  };
  const signOutOthers = async () => {
    if (!(await confirm('Sign out every other device?', { body: 'This device stays signed in.', ok: 'Sign out others', danger: true }))) return;
    try { const r = await post<{ revoked: number }>('/api/auth/sessions/revoke-others'); toast(`Signed out ${r.revoked} other device${r.revoked === 1 ? '' : 's'}`); qc.invalidateQueries({ queryKey: ['security'] }); }
    catch (e) { toast((e as Error).message, true); }
  };
  if (!me) return null;
  const others = sec?.sessions.filter((d) => !d.current).length ?? 0;

  return (
    <Page title="Your account" eyebrow={ROLE_LABELS[me.role]}>
      <div className="grid-2">
        <section className="card pad stack">
          <h2>Profile</h2>
          <form className="stack" onSubmit={(e: FormEvent) => { e.preventDefault(); saveName.mutate(); }}>
            <label className="field"><span>Name</span><input className="input" value={name} onChange={(e) => setName(e.target.value)} required maxLength={120} /></label>
            <label className="field"><span>Email</span><input className="input" value={me.email} disabled /><span className="hint">Ask an admin if this needs to change.</span></label>
            <div><button className="btn primary" disabled={saveName.isPending || name.trim() === me.name}>Save</button></div>
          </form>
        </section>
        {me.emails.length > 0 && (
          <section className="card pad stack">
            <h2>Email</h2>
            <p className="muted small" style={{ margin: 0 }}>Emails go to {me.email}. Turn off any you don't want.</p>
            {me.emails.map((e) => (
              <label key={e.key} className="row" style={{ gap: 10, alignItems: 'flex-start' }}>
                <input type="checkbox" checked={e.on} disabled={setEmail.isPending} onChange={(ev) => setEmail.mutate({ key: e.key, on: ev.target.checked })} style={{ marginTop: 3 }} />
                <span><span className="small">{e.label}</span><br /><span className="muted tiny">{e.hint}</span></span>
              </label>
            ))}
          </section>
        )}
        <section className="card pad stack">
          <h2>Password</h2>
          <form className="stack" onSubmit={(e: FormEvent) => { e.preventDefault(); changePw.mutate(); }}>
            <label className="field"><span>Current password</span><input className="input" type="password" autoComplete="current-password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} required /></label>
            <label className="field"><span>New password</span><input className="input" type="password" autoComplete="new-password" minLength={12} value={pw.new} onChange={(e) => setPw({ ...pw, new: e.target.value })} required /><span className="hint">At least 12 characters. Other devices are signed out.</span></label>
            <div><button className="btn primary" disabled={changePw.isPending}>{changePw.isPending && <span className="spin" />}Change password</button></div>
          </form>
        </section>
        <section className="card pad stack">
          <h2>Two-factor sign-in</h2>
          {sec && (sec.totp_enabled ? (
            <>
              <p className="muted small" style={{ margin: 0 }}>On. You sign in with your password and a code from your authenticator app.</p>
              <p className="small" style={{ margin: 0 }}>
                <b>{sec.recovery_codes_left}</b> unused recovery code{sec.recovery_codes_left === 1 ? '' : 's'}
                {sec.recovery_codes_left <= 3 && <span className="error-text"> · make new ones soon</span>}
              </p>
              <div><button className="btn" onClick={() => setRegenerating(true)}>Make new recovery codes</button></div>
            </>
          ) : <p className="muted small" style={{ margin: 0 }}>Off. {me.is_staff ? 'You will be asked to set it up.' : 'Ask Blue Market if you would like to turn it on.'}</p>)}
        </section>
        <section className="card pad stack">
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <h2>Signed-in devices</h2>
            {others > 0 && <button className="btn sm danger ghost" onClick={signOutOthers}>Sign out others</button>}
          </div>
          <div>
            {sec?.sessions.map((d) => (
              <div className="device" key={d.id}>
                <div className="grow">
                  <div><b>{deviceName(d.user_agent)}</b>{d.current && <span className="badge info" style={{ marginLeft: 8 }}>This device</span>}</div>
                  <div className="muted small">{d.ip ?? 'unknown address'} · signed in {ago(d.created_at)} · last active {ago(d.last_seen_at)}</div>
                </div>
                {!d.current && <button className="btn sm ghost" onClick={() => signOutDevice(d)}>Sign out</button>}
              </div>
            ))}
          </div>
        </section>
        <ConnectedAccounts />
        {!me.is_staff && me.client && can('data.export_own') && (
          <ExportsCard clientId={me.client.id} title={`${me.client.name}'s data`}
                       blurb={`Everything ${me.client.name}'s people can see here: projects, tickets and comments, shared designs, questions and answers, and the activity log.`} />
        )}
      </div>
      {regenerating && <RegenerateCodes onClose={() => { setRegenerating(false); qc.invalidateQueries({ queryKey: ['security'] }); }} />}
      {dialog}
    </Page>
  );
}

function RegenerateCodes({ onClose }: { onClose: () => void }) {
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setError('');
    try { setCodes((await post<{ recovery_codes: string[] }>('/api/auth/recovery-codes', { code })).recovery_codes); }
    catch (err) { setError(err instanceof Error ? err.message : 'Something went wrong'); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={codes ? 'New recovery codes' : 'Make new recovery codes'} onClose={onClose} width={460}>
      {codes ? <RecoveryCodes codes={codes} onDone={onClose} /> : (
        <form className="stack" onSubmit={submit}>
          <p className="muted small" style={{ margin: 0 }}>Your old codes stop working straight away. Enter the six-digit code from your authenticator app to continue.</p>
          <input className="input code-input" inputMode="numeric" autoComplete="one-time-code" maxLength={7} value={code} onChange={(e) => setCode(e.target.value)} autoFocus required aria-label="Six-digit code" />
          {error && <div className="error">{error}</div>}
          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
            <button className="btn primary" disabled={busy}>{busy && <span className="spin" />}Make new codes</button>
          </div>
        </form>
      )}
    </Modal>
  );
}


interface Identity { name: string; label: string; available: boolean; connected: { email: string; since: string; last_used_at: string | null } | null }

/** Google / Microsoft accounts that can sign you in. Only shown once the platform has them set up. */
function ConnectedAccounts() {
  const toast = useToast();
  const qc = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const { data } = useQuery({ queryKey: ['identities'], queryFn: () => get<{ providers: Identity[] }>('/api/auth/sso/identities') });
  useEffect(() => {
    const h = takeSsoHash();
    if (h['sso-linked']) toast(`Connected. You can now sign in with ${h['sso-linked'] === 'google' ? 'Google' : 'Microsoft'}.`);
    if (h['sso-error']) toast(SSO_ERRORS[h['sso-error']] ?? "That didn't work. Try again.", true);
  }, [toast]);
  if (!data?.providers.length) return null;
  const disconnect = async (p: Identity) => {
    if (!(await confirm(`Disconnect ${p.label}?`, { body: "You'll sign in with your password from now on.", ok: 'Disconnect', danger: true }))) return;
    try { qc.setQueryData(['identities'], await del(`/api/auth/sso/identities/${p.name}`)); } catch (e) { toast((e as Error).message, true); }
  };
  return (
    <section className="card pad stack" style={{ gap: 10 }}>
      <h2>Sign in with Google or Microsoft</h2>
      <p className="muted small" style={{ margin: 0 }}>Connect your work account to sign in without your password. If you use a six-digit code, you'll still enter it.</p>
      {data.providers.map((p) => (
        <div key={p.name} className="row small" style={{ gap: 8 }}>
          <b style={{ minWidth: 80 }}>{p.label}</b>
          <span className="grow muted">{p.connected ? `${p.connected.email} · ${p.connected.last_used_at ? `last used ${ago(p.connected.last_used_at)}` : 'not used yet'}` : 'Not connected'}</span>
          {p.connected ? <button className="btn sm ghost" onClick={() => disconnect(p)}>Disconnect</button>
            : p.available && <a className="btn sm" href={`/api/auth/sso/${p.name}/start?mode=link`}>Connect</a>}
        </div>
      ))}
      {dialog}
    </section>
  );
}
